/**
 * Раскрытие — единственный жест «показать подробность на месте».
 *
 * Ради чего компонент существует, а не пишется на каждом экране заново:
 *
 * * **сводка обязательна.** Свёрнутый блок не имеет права быть просто
 *   заголовком: в закрытом виде он показывает ответ («39 категорий, 46 738,78
 *   га»), и человек решает раскрывать по ответу, а не по названию;
 * * **скрытое названо числом.** Если внутри 22 строки, снаружи так и написано.
 *   Иначе перенос вглубь неотличим от удаления;
 * * **жест один и тот же везде.** Район, массив, хозяйство, контур и качество
 *   данных раскрываются одинаково, потому что раскрывает их один компонент.
 *
 * Внутри — нативные `<details>/<summary>`: клавиатура, скринридер и поиск по
 * странице работают без единой строки собственного кода, а собственная
 * реализация «аккордеона» их бы сломала.
 */

import type { ReactNode } from "react";
import styles from "./disclosure.module.css";

export function Disclosure({
  title,
  summary,
  children,
  open = false,
  tone = "card",
  more,
  onToggle,
}: {
  /** Что это за блок. */
  title: ReactNode;
  /** Ответ в свёрнутом виде: величины, ради которых блок вообще открывают. */
  summary?: ReactNode;
  children: ReactNode;
  /** Раскрыт ли по умолчанию. По умолчанию — нет: первый взгляд короче. */
  open?: boolean;
  /** `card` — самостоятельный блок экрана, `inline` — вложенное раскрытие. */
  tone?: "card" | "inline";
  /** Подпись действия: «показать все 22». Скрытое обязано быть названо. */
  more?: ReactNode;
  /**
   * Сообщить экрану о раскрытии. Нужно ровно для одного случая: содержимое,
   * которое стоит дорого (кадр карты — отдельный запрос тайлов), монтируется
   * только после открытия. Свёрнутый `<details>` держит детей в DOM, поэтому
   * без этого «свернули» не означало бы «не запрашивали».
   */
  onToggle?: (open: boolean) => void;
}) {
  return (
    <details
      className={tone === "card" ? styles.card : styles.inline}
      open={open}
      onToggle={(event) => onToggle?.(event.currentTarget.open)}
    >
      <summary className={styles.summary}>
        <span className={styles.marker} aria-hidden />
        <span className={styles.head}>
          <span className={styles.title}>{title}</span>
          {summary ? <span className={styles.answer}>{summary}</span> : null}
        </span>
        {more ? <span className={styles.more}>{more}</span> : null}
      </summary>
      <div className={styles.body}>{children}</div>
    </details>
  );
}
